import { type FormikErrors } from 'formik'
import type { ILeadFormModel } from '../typings'

import { isValidMuiDate, updatePhoneNumber } from '@sales-center/common'
import { validateLocation } from '../FormComponents/LocationInformation/hooks/validateLocation'
import {
  FieldSettingsMap,
  IEntityFormSetting,
} from '@user/store/entityFormSettings'
import { defaultFieldNames } from '../FormComponents/LocationInformation/typings'
import {
  addLeadNote,
  leadStatusDisqualified,
} from '@user/components/Settings/EntityFormSettings/constants'
import type { NameRequirementMode } from '@user/components/Settings/EntityFormSettings/config/nameRequirementMode'
import { validateEmailCollection } from '@user/components/EmailCollection'

export function validateNoCustomSettings(
  model: ILeadFormModel,
  requiredEntitySettings: IEntityFormSetting[],
  settingsMap: FieldSettingsMap,
  validatePostalCode: (postalCode: string) => string | undefined,
  nameRequirementMode: NameRequirementMode,
): FormikErrors<ILeadFormModel> {
  const errors: FormikErrors<ILeadFormModel> = {}

  const [requiredAddNote] = settingsMap.get(addLeadNote) ?? [false, true]

  const {
    salesTeamId,
    salesFunnelId,
    assigneeId,

    salesFunnelStageId,

    originalContactMethod,
    nextActionDate,
    externalLocationId,

    note,
    noteCode,
    addNoteToPestPac,
    salesFunnelStageName,
    reasonId,
    customReason,
    locationSameAsBillTo,
    externalLocationCode,

    referral,
  } = model

  // location
  const locationErrors = validateLocation(
    defaultFieldNames,
    '',
    requiredEntitySettings,
    settingsMap,
    model,
    validatePostalCode,
    nameRequirementMode,
  )

  const billTolocationErrors =
    externalLocationCode || locationSameAsBillTo
      ? []
      : validateLocation(
          defaultFieldNames,
          'billTo_',
          requiredEntitySettings,
          settingsMap,
          model,
          validatePostalCode,
          nameRequirementMode,
        )

  // ownership info
  if (salesTeamId == '') errors.salesTeamId = 'Required'
  if (salesFunnelId == '') errors.salesFunnelId = 'Required'
  if (assigneeId == '') errors.assigneeId = 'Required'

  // attribution
  if (salesFunnelStageId == '') errors.salesFunnelStageId = 'Required'

  // other info
  if (originalContactMethod.length > 10)
    errors.originalContactMethod = `${originalContactMethod.length}/10`

  if (nextActionDate != null && !isValidMuiDate(nextActionDate))
    errors.nextActionDate = 'Invalid data format'
  if (externalLocationId) validateModelFromExternalLocation(model, errors)

  if (requiredAddNote) {
    if (note?.trim() === '') errors.note = 'Required'
  }

  if (note?.trim() !== '' && addNoteToPestPac) {
    if (!noteCode) errors.noteCode = 'Required'
  }

  if (
    salesFunnelStageName !== '' &&
    salesFunnelStageName === leadStatusDisqualified
  ) {
    if (reasonId === -1) {
      errors.reasonId = 'Disqualified reason required*'
    } else if (reasonId === '0') {
      if (customReason === '') {
        errors.reasonId = 'Disqualified reason required*'
        errors.customReason =
          'Max character limit is 50. Your current count is 0/50'
      }
    }
  }

  if (referral) {
    const { referralType, referralValue } = referral
    switch (referralType) {
      case 'Customer':
      case 'Other':
        if (!referralValue || referralValue.length <= 0) {
          errors.referral = 'Required'
        }
        break
      case 'Employee':
        if (!referralValue || referralValue.length <= 0) {
          errors.referral = 'Required'
        }
        break
      default:
        break
    }
  }

  return { ...errors, ...locationErrors, ...billTolocationErrors }
}

export function validateModelFromExternalLocation(
  values: ILeadFormModel,
  errors: FormikErrors<ILeadFormModel>,
): void {
  const {
    firstName,
    lastName,
    company,
    phoneNumber,
    phoneExt,
    altPhoneNumber,
    altPhoneExt,
    emailAddresses,
  } = values

  if (firstName.trim() !== '' && firstName.length > 15)
    errors.firstName = 'First name must be between 1 and 15 characters'
  if (lastName.trim() !== '' && lastName.length > 20)
    errors.lastName = 'Last name must be between 1 and 20 characters'
  if (company.trim() !== '' && company.length > 35)
    errors.company = 'Company must be between 1 and 35 characters'
  if (phoneNumber.trim() !== '' && updatePhoneNumber(phoneNumber).length > 12)
    errors.phoneNumber = 'Phone number must be between 1 and 12 characters'
  if (phoneExt.trim() !== '' && phoneExt.length > 5)
    errors.phoneExt = 'Phone extension must be between 1 and 5 characters'
  if (
    altPhoneNumber.trim() !== '' &&
    updatePhoneNumber(altPhoneNumber).length > 12
  )
    errors.altPhoneNumber =
      'Alt Phone number must be between 1 and 12 characters'
  if (altPhoneExt.trim() !== '' && altPhoneExt.length > 5)
    errors.altPhoneExt =
      'Alt Phone extension must be between 1 and 5 characters'
  const emailErrors = validateEmailCollection(
    emailAddresses,
    false,
    true,
    false,
  )
  if (emailErrors) errors.emailAddresses = emailErrors
}
